import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { isNativeApp } from '../../utils/platform';
import WebOnlyNotice from '../common/WebOnlyNotice';
import ConfirmModal from '../common/ConfirmModal';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const SubscriptionManagement = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isOnboarding = searchParams.get('onboarding') === 'true';

  const [tiers, setTiers] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTier, setSelectedTier] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [showPayment, setShowPayment] = useState(false);
  const [setupIntentSecret, setSetupIntentSecret] = useState('');
  const [subscriptionConfirm, setSubscriptionConfirm] = useState({ isOpen: false, action: null, tierId: null, message: '' });

  useEffect(() => {
    fetchTiers();
    fetchCurrentSubscription();
  }, []);

  const fetchTiers = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/subscriptions/tiers`);
      setTiers(response.data);
    } catch (error) {
      console.error('Error fetching tiers:', error);
    }
  };

  const fetchCurrentSubscription = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/subscriptions/current`);
      setCurrentSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (tierId) => {
    setSelectedTier(tierId);

    try {
      // Create setup intent to collect payment method
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/subscriptions/create-setup-intent`,
        {},
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      setSetupIntentSecret(response.data.client_secret);
      setShowPayment(true);
    } catch (error) {
      console.error('Error creating setup intent:', error);
      alert(error.response?.data?.detail || 'Failed to start subscription process');
    }
  };

  const promptUpgrade = (tierId) => {
    const selectedTier = tiers.find(t => t.id === tierId);
    const priceText = billingCycle === 'annual'
      ? `$${selectedTier.annual_fee}/year`
      : `$${selectedTier.monthly_fee}/month`;

    const changingCycle = currentSubscription.subscription_billing_cycle !== billingCycle;

    let message = `Upgrade to ${selectedTier.tier_name} (${priceText})?`;

    if (changingCycle) {
      message += ` You'll also switch from ${currentSubscription.subscription_billing_cycle} to ${billingCycle} billing. You'll receive credit for unused time on your current plan.`;
    } else {
      message += ' You will be charged a prorated amount immediately.';
    }

    setSubscriptionConfirm({ isOpen: true, action: 'upgrade', tierId, message });
  };

  const handleUpgrade = async (tierId) => {
    const selectedTier = tiers.find(t => t.id === tierId);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/subscriptions/upgrade`,
        {
          new_tier_id: tierId,
          billing_cycle: billingCycle  // Send the selected billing cycle
        }
      );

      if (response.data.status === 'recovered_and_upgraded') {
        alert(`Your subscription was restored and upgraded to ${selectedTier.tier_name}!`);
      } else if (response.data.status === 'upgraded_with_cycle_change') {
        alert(`Successfully upgraded to ${selectedTier.tier_name} with ${billingCycle} billing!`);
      } else {
        alert(`Successfully upgraded to ${selectedTier.tier_name}!`);
      }

      fetchCurrentSubscription();
    } catch (error) {
      console.error('Error upgrading:', error);
      const errorDetail = error.response?.data?.detail || '';
      if (errorDetail.includes('PAYMENT_METHOD_REQUIRED')) {
        alert('No payment method on file. Please add a payment method first.');
      } else if (errorDetail.includes('SUBSCRIPTION_NOT_FOUND')) {
        alert('Subscription data was out of sync. Please try again.');
        fetchCurrentSubscription();
      } else {
        alert(errorDetail || 'Failed to upgrade');
      }
    }
  };

  const handleDowngrade = async (tierId) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/subscriptions/downgrade`,
        { new_tier_id: tierId }
      );

      if (response.data.status === 'recovered_and_downgraded') {
        alert(response.data.message || 'Subscription restored at the new tier!');
      } else {
        const downgradeDate = new Date(response.data.downgrade_date).toLocaleDateString();
        alert(`Downgrade scheduled for ${downgradeDate}`);
      }
      fetchCurrentSubscription();
    } catch (error) {
      console.error('Error downgrading:', error);
      const errorDetail = error.response?.data?.detail || '';
      if (errorDetail.includes('PAYMENT_METHOD_REQUIRED')) {
        alert('No payment method on file. Please add a payment method first.');
      } else if (errorDetail.includes('SUBSCRIPTION_NOT_FOUND')) {
        alert('Subscription data was out of sync. Please try again.');
        fetchCurrentSubscription();
      } else {
        alert(errorDetail || 'Failed to schedule downgrade');
      }
    }
  };

  const handleCancel = async () => {
    try {
      const response = await axios.delete(`${process.env.REACT_APP_API_URL}/api/subscriptions/cancel`);

      if (response.data.status === 'already_cancelled') {
        alert('Subscription was already cancelled. You have been reverted to the free tier.');
      } else {
        const cancelDate = new Date(response.data.cancel_at).toLocaleDateString();
        alert(`Subscription will cancel on ${cancelDate}`);
      }
      fetchCurrentSubscription();
    } catch (error) {
      console.error('Error canceling:', error);
      alert(error.response?.data?.detail || 'Failed to cancel subscription');
    }
  };

  if (loading) {
    return <div className="text-center p-8">Loading subscription information...</div>;
  }

  // Don't show subscription management on native apps (App Store compliance)
  if (isNativeApp()) {
    return <WebOnlyNotice
      message="Subscription management is available on the web version of RideHowl. Please log in on a web browser to manage your subscription plan."
      backTo="/driver/dashboard"
    />;
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header with Back Button */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Manage Your Subscription</h1>
        {isOnboarding ? (
          <button
            onClick={() => {
              // When returning from subscription during onboarding, go to dashboard with tutorial
              const tutorialCompleted = localStorage.getItem('driver_tutorial_completed');
              if (!tutorialCompleted) {
                navigate('/driver/dashboard?showTutorial=true');
              } else {
                navigate('/driver/dashboard');
              }
            }}
            className="inline-flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
            Go to Dashboard
          </button>
        ) : (
          <Link
            to="/driver/dashboard"
            className="inline-flex items-center px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Dashboard
          </Link>
        )}
      </div>

      {/* Current Subscription Info */}
      {currentSubscription && currentSubscription.tier_name !== 'Starter' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">Current Plan</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-gray-600">Tier</p>
              <p className="font-semibold text-lg">{currentSubscription.tier_name}</p>
            </div>
            <div>
              <p className="text-gray-600">Platform Fee</p>
              <p className="font-semibold text-lg">{currentSubscription.commission_rate}%</p>
            </div>
            <div>
              <p className="text-gray-600">Billing Cycle</p>
              <p className="font-semibold capitalize">{currentSubscription.subscription_billing_cycle || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-600">Status</p>
              <p className="font-semibold capitalize">{currentSubscription.subscription_status || 'Active'}</p>
            </div>
          </div>
          
          {currentSubscription.current_period_end && (
            <p className="mt-4 text-sm text-gray-600">
              Next billing date: {new Date(currentSubscription.current_period_end).toLocaleDateString()}
            </p>
          )}
          
          {currentSubscription.subscription_status && (
            <button
              onClick={() => setSubscriptionConfirm({ isOpen: true, action: 'cancel', tierId: null, message: 'Cancel subscription? You will revert to free tier at the end of your billing period.' })}
              className="mt-4 bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
            >
              Cancel Subscription
            </button>
          )}
        </div>
      )}
      
      {/* Free Tier Notice */}
      {currentSubscription && currentSubscription.tier_name === 'Starter' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold mb-2">You're on the Free Tier</h2>
          <p className="text-gray-700">
            Currently paying {currentSubscription.commission_rate}% platform fee per ride.
            Upgrade to a paid tier to lower your platform fees!
          </p>
        </div>
      )}
      
      {/* Billing Cycle Toggle */}
      <div className="flex justify-center mb-8">
        <div className="bg-gray-200 rounded-lg p-1 inline-flex">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-6 py-2 rounded-lg transition ${
              billingCycle === 'monthly'
                ? 'bg-white shadow-md text-blue-600 font-semibold'
                : 'text-gray-600'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('annual')}
            className={`px-6 py-2 rounded-lg transition ${
              billingCycle === 'annual'
                ? 'bg-white shadow-md text-blue-600 font-semibold'
                : 'text-gray-600'
            }`}
          >
            Annual <span className="text-green-600 font-semibold">(Save 10%)</span>
          </button>
        </div>
      </div>
      
      {/* Tier Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
        {tiers.map((tier) => {
          const isCurrentTier = currentSubscription && Number(currentSubscription.subscription_tier_id) === Number(tier.id);
          const price = billingCycle === 'annual' ? tier.annual_fee : tier.monthly_fee;
          const monthlyEquivalent = billingCycle === 'annual' ? (tier.annual_fee / 12).toFixed(2) : price;
          
          return (
            <div
              key={tier.id}
              className={`border-2 rounded-lg p-6 ${
                isCurrentTier
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300 hover:shadow-lg transition'
              }`}
            >
              <h3 className="text-2xl font-bold mb-2">{tier.tier_name}</h3>
              
              <div className="mb-4">
                <p className="text-3xl font-bold">
                  ${billingCycle === 'annual' ? monthlyEquivalent : price}
                  <span className="text-sm text-gray-600">/mo</span>
                </p>
                {billingCycle === 'annual' && tier.tier_name !== 'Starter' && (
                  <p className="text-sm text-gray-600">
                    Billed ${tier.annual_fee} annually
                  </p>
                )}
              </div>
              
              <div className="mb-6">
                <p className="text-lg font-semibold text-blue-600">
                  {tier.commission_rate}% platform fee
                </p>
                <p className="text-sm text-gray-600">per ride</p>
              </div>
              
              {tier.tier_name === 'Starter' ? (
                <p className="text-center text-gray-600 font-semibold">Free Forever</p>
              ) : isCurrentTier ? (
                <div className="w-full bg-gray-200 text-gray-500 px-4 py-2 rounded text-center font-medium">
                  Current Active Tier
                </div>
              ) : !currentSubscription || currentSubscription.tier_name === 'Starter' ? (
                <button
                  onClick={() => handleSubscribe(tier.id)}
                  className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
                >
                  Subscribe
                </button>
              ) : tier.tier_level < currentSubscription.tier_level ? (
                <button
                  onClick={() => promptUpgrade(tier.id)}
                  className="w-full bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition"
                >
                  Upgrade
                </button>
              ) : (
                <button
                  onClick={() => setSubscriptionConfirm({ isOpen: true, action: 'downgrade', tierId: tier.id, message: 'Downgrade at the end of your current billing period?' })}
                  className="w-full bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700 transition"
                >
                  Downgrade
                </button>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Payment Form Modal */}
      {showPayment && setupIntentSecret && (
        <Elements stripe={stripePromise} options={{ clientSecret: setupIntentSecret }}>
          <PaymentForm
            tierId={selectedTier}
            billingCycle={billingCycle}
            onSuccess={() => {
              setShowPayment(false);
              fetchCurrentSubscription();
            }}
            onCancel={() => setShowPayment(false)}
          />
        </Elements>
      )}

      <ConfirmModal
        isOpen={subscriptionConfirm.isOpen}
        onClose={() => setSubscriptionConfirm({ isOpen: false, action: null, tierId: null, message: '' })}
        onConfirm={() => {
          const { action, tierId } = subscriptionConfirm;
          if (action === 'upgrade') handleUpgrade(tierId);
          else if (action === 'downgrade') handleDowngrade(tierId);
          else if (action === 'cancel') handleCancel();
        }}
        title={
          subscriptionConfirm.action === 'upgrade' ? 'Confirm Upgrade' :
          subscriptionConfirm.action === 'downgrade' ? 'Confirm Downgrade' :
          'Cancel Subscription'
        }
        message={subscriptionConfirm.message}
        confirmText={
          subscriptionConfirm.action === 'upgrade' ? 'Upgrade' :
          subscriptionConfirm.action === 'downgrade' ? 'Downgrade' :
          'Cancel Subscription'
        }
        confirmColor={subscriptionConfirm.action === 'cancel' ? 'red' : 'green'}
      />
    </div>
  );
};

// Payment Form Component
const PaymentForm = ({ tierId, billingCycle, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeId, setPromoCodeId] = useState(null);
  const [promoValidating, setPromoValidating] = useState(false);
  const [promoDiscount, setPromoDiscount] = useState(null);
  const [promoError, setPromoError] = useState('');

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;

    setPromoValidating(true);
    setPromoError('');
    setPromoDiscount(null);
    setPromoCodeId(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/subscriptions/validate-promo`,
        { code: promoCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.valid) {
        setPromoCodeId(response.data.promotion_code_id);
        setPromoDiscount(response.data);
      } else {
        setPromoError(response.data.error || 'Invalid promo code');
      }
    } catch (error) {
      setPromoError('Failed to validate promo code');
    } finally {
      setPromoValidating(false);
    }
  };

  const handleRemovePromo = () => {
    setPromoCode('');
    setPromoCodeId(null);
    setPromoDiscount(null);
    setPromoError('');
  };

  const formatDiscount = (discount) => {
    if (!discount) return '';
    if (discount.percent_off) {
      if (discount.duration === 'forever') {
        return `${discount.percent_off}% off forever`;
      } else if (discount.duration === 'repeating') {
        return `${discount.percent_off}% off for ${discount.duration_in_months} months`;
      } else {
        return `${discount.percent_off}% off first payment`;
      }
    } else if (discount.amount_off) {
      const amount = (discount.amount_off / 100).toFixed(2);
      return `$${amount} off`;
    }
    return 'Discount applied';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }
    
    setProcessing(true);
    setErrorMessage('');
    
    try {
      // Confirm the setup intent to get the payment method
      const { setupIntent, error: setupError } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
      });
      
      if (setupError) {
        setErrorMessage(setupError.message);
        setProcessing(false);
        return;
      }
      
      // Create the subscription with the payment method (and promo code if applied)
      const subscriptionData = {
        tier_id: tierId,
        billing_cycle: billingCycle,
        payment_method_id: setupIntent.payment_method
      };

      // Include promo code if one was validated
      if (promoCodeId) {
        subscriptionData.promotion_code = promoCodeId;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/subscriptions/subscribe`,
        subscriptionData
      );
      
      alert('Subscription created successfully!');
      onSuccess();
      
    } catch (error) {
      console.error('Subscription error:', error);
      setErrorMessage(error.response?.data?.detail || 'Failed to create subscription');
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-6">Complete Payment</h2>
        
        <form onSubmit={handleSubmit}>
          <PaymentElement />

          {/* Promo Code Section */}
          <div className="mt-6 pt-4 border-t">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Have a promo code?
            </label>

            {promoDiscount ? (
              <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                <div>
                  <span className="font-medium text-green-800">{promoDiscount.coupon_name}</span>
                  <p className="text-sm text-green-600">{formatDiscount(promoDiscount)}</p>
                </div>
                <button
                  type="button"
                  onClick={handleRemovePromo}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  placeholder="Enter promo code"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={promoValidating}
                />
                <button
                  type="button"
                  onClick={handleApplyPromo}
                  disabled={promoValidating || !promoCode.trim()}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition"
                >
                  {promoValidating ? 'Checking...' : 'Apply'}
                </button>
              </div>
            )}

            {promoError && (
              <p className="mt-2 text-sm text-red-600">{promoError}</p>
            )}
          </div>

          {errorMessage && (
            <div className="mt-4 p-3 bg-red-100 text-red-700 rounded">
              {errorMessage}
            </div>
          )}

          <div className="flex gap-4 mt-6">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
              disabled={processing}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!stripe || processing}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400"
            >
              {processing ? 'Processing...' : 'Subscribe'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubscriptionManagement;